import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import cn from 'classnames'
import { normalizeCount } from 'util/favicon'

import useFeatureDot from 'shared/ui/useFeatureDot'
import { IconButton } from 'shared/ui'

const GlobalNavItemContainer = styled('div')`
  line-height: 0;
  position: relative;

  &&.active:not(.basic) {
    background-color: ${props =>
      props.theme.color.globalNav.iconActiveBackground};
  }

  &&.active:not(.basic)::before {
    content: "";
    position: absolute;
    background-color: transparent;
    top: -48px;
    right: 0px;
    height: 48px;
    width: 6px;
    border-bottom-right-radius: 6px;
    box-shadow: 0 6px 0 0 ${props =>
      props.theme.color.globalNav.iconActiveBackground};
  }

  &&.active:not(.basic)::after {
    content: "";
    position: absolute;
    background-color: transparent;
    right: 0;
    bottom: -48px;
    height: 48px;
    width: 6px;
    border-top-right-radius: 6px;
    box-shadow: 0 -6px 0 0 ${props =>
      props.theme.color.globalNav.iconActiveBackground};;
  }

  &&.active:not(.basic) > div:first-child:before {
    border: solid 2px ${props =>
      props.theme.color.globalNav.iconActiveBackground};
  }

  > div:first-child:before {
    display: ${({ noticeCount }) => (noticeCount ? 'block' : 'none')};
    content: "${({ noticeCount }) => noticeCount}";
    position: absolute;
    top: 6px;
    right: 4px;
    background: ${props => props.theme.color.globalNav.noticeCountBackground};
    border: solid 2px ${props => props.theme.color.globalNav.noticeCountColor};
    border-radius: 10px;
    height: auto;
    font-size: ${props => props.theme.fontSize.xtiny};
    font-weight: ${props => props.theme.fontWeight.medium};
    color: ${props => props.theme.color.globalNav.noticeCountColor};
    line-height: 1;
    text-align: center;
    padding: 2px;
    min-width: 16px;
    z-index: 2;
  }
`

const IconButtonStyle = css`
  border-radius: 0;
  &&&::before {
    bottom: 4px;
    left: 4px;
    right: 4px;
    top: 4px;
  }
`

export default function GlobalNavItem({
  iconName,
  isActive,
  onClick,
  isLegacy,
  to,
  title,
  size = 16,
  tooltipDisabled = false,
  noticeCount = 0,
  featureDotFlag,
  isLeftSidebarCollapsed,
}) {
  // NOTE (jscheel): Because we roll out new subapps slowly, we use the
  // limitedScope version of useFeatureDot. Therefore we must set the agent flag
  // for the appropriate agents in the rollout.
  const { dotTargetRef, dotMarkAsSeen, Dot } = useFeatureDot(
    featureDotFlag,
    true
  )
  const handleClick = useCallback(
    e => {
      dotMarkAsSeen()
      if (onClick) onClick(e)
    },
    [onClick, dotMarkAsSeen]
  )

  return (
    <GlobalNavItemContainer
      data-test-id={title}
      isActive={isActive}
      className={cn({ active: isActive, basic: isLeftSidebarCollapsed })}
      onClick={handleClick}
      noticeCount={normalizeCount(noticeCount)}
    >
      <IconButton
        name={iconName}
        iconSize={size}
        size="large"
        svg
        to={to}
        isLegacy={isLegacy}
        tooltip={title}
        tooltipDisabled={tooltipDisabled}
        tooltipPosition="right"
        active={isActive}
        className={cn({ active: isActive })}
        css={IconButtonStyle}
        color={
          isActive &&
          (isLeftSidebarCollapsed
            ? 'color.globalNav.iconActiveBasic'
            : 'color.globalNav.iconActive')
        }
        colorHover="color.globalNav.iconActiveBasic"
        iconRef={featureDotFlag && dotTargetRef}
      />
      {featureDotFlag && <Dot />}
    </GlobalNavItemContainer>
  )
}
